<template>
    <div>
        <div class="analytics-table">
            <Loader :show="isLoading"/>
            <Txt weight="bold" class="analytics-card-title mb-3" no-margin>{{$t('Dashboard.Charts.ActivityPerTarget')}}</Txt>
        
             <Modal
                ref="cgTargetModal"
                v-model:open="showTargetModal"
                :trigger="selTargetRef"
                with-close-button
                with-expand-button
                full-height
                large
            >
                <Txt weight="bold" class="analytics-card-title mb-3" no-margin>{{$t('Dashboard.Charts.ActivityPerTargetName', { name: selectedTargetName })}}</Txt>

                <div class="grid grid-cols-10 gap-0 text-xs font-bold text-violet-700 border-b border-soft-blue-300">
                    <div class="timeline-header">{{ $t('Analytics.Campaign') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.Table.SentStats') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.Table.OpenStats') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.Table.ClickedStats') }}</div>
                    <div class="timeline-header">{{ $t('Dashboard.Charts.ReportedStats') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.Table.SubmittedDataStats') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.Device') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.OS') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.Browser') }}</div>
                    <div class="timeline-header">{{ $t('Analytics.IP') }}</div>
                </div>

                <div v-for="(row, i) in modalTableData" :key="i" class="grid grid-cols-10 gap-0 text-xs border-b border-soft-blue-300 odd:bg-white/50 h-[70px]">
                    <div class="flex items-center">{{ row.name }}</div>

                    <div class="timeline-row" @click="editCell(i, 'sent')">
                        <span v-if="!isEditingCell(i, 'sent')">{{ row.sent }}</span>
                        <CGInput v-else v-model="row.sent" class="w-full" type="datetime-local" />
                    </div>

                    <div class="timeline-row" @click="editCell(i, 'open')">
                        <span v-if="!isEditingCell(i, 'open')">{{ row.open }}</span>
                        <CGInput v-else v-model="row.open" class="w-full" type="datetime-local" />
                    </div>

                    <div class="timeline-row" :class="{ 'bg-red-100': row.constraint }" @click="editCell(i, 'clicked')">
                        <span v-if="!isEditingCell(i, 'clicked')">{{ row.clicked }}</span>
                        <CGInput v-else v-model="row.clicked" class="w-full" type="datetime-local"
                            :errored="row.constraint" @update:modelValue="() => row.constraint = checkConstraints()" />
                    </div>

                    <div class="timeline-row" :class="{ 'bg-red-100': row.constraint }" @click="editCell(i, 'reported')">
                        <span v-if="!isEditingCell(i, 'reported')">{{ row.reported }}</span>
                        <CGInput v-else v-model="row.reported" class="w-full" type="datetime-local"
                            :errored="row.constraint" @update:modelValue="() => row.constraint = checkConstraints()" />
                    </div>

                    <div class="timeline-row" @click="editCell(i, 'submitted_data')">
                        <span v-if="!isEditingCell(i, 'submitted_data')">{{ row.submitted_data }}</span>
                        <CGInput v-else v-model="row.submitted_data" class="w-full" type="datetime-local" />
                    </div>

                    <div class="timeline-row" @click="editCell(i, 'phished_device')">
                        <span v-if="!isEditingCell(i, 'phished_device')">{{ row.phished_device_label }}</span>
                        <CGSimpleSelect v-else v-model.number="row.phished_device" class="w-full" :options="deviceOptions" @update:modelValue="setPhishedDeviceLabel" />
                    </div>

                    <div class="timeline-row" @click="editCell(i, 'phished_os')">
                        <span v-if="!isEditingCell(i, 'phished_os')">{{ row.phished_os }}</span>
                        <CGInput v-else v-model="row.phished_os" class="w-full" type="text" />
                    </div>

                    <div class="timeline-row" @click="editCell(i, 'phished_browser')">
                        <span v-if="!isEditingCell(i, 'phished_browser')">{{ row.phished_browser }}</span>
                        <CGInput v-else v-model="row.phished_browser" class="w-full" type="text" />
                    </div>

                    <div class="timeline-row" :class="{ 'bg-red-100': row.ipError }" @click="editCell(i, 'phished_ip')">
                        <span v-if="!isEditingCell(i, 'phished_ip')">{{ row.phished_ip }}</span>
                        <CGInput v-else v-model="row.phished_ip" class="w-full" type="text"
                            :errored="row.ipError" :error-message="row.ipError ? $t('General.InvalidIP') : null"
                            @update:modelValue="value => row.ipError = !validateIP(value)" />
                    </div>
                </div>

                <template #footer>
                    <div class="mt-8 text-center ml-auto">
                        <CGButton ref="confirmOverrideRef" :disabled="disableSaveButton" @click="confirmModal=true">{{ $t('General.Save') }}</CGButton>
                    </div>
                </template>
            </Modal>

            <!-- Confirm modal -->
            <Modal v-model:open="confirmModal" :title="$t('Targets.ConfirmDeleteTitle')" :trigger="confirmOverrideRef" :description="$t('Targets.ConfirmOverrideMessage')" footer-alignment="center" small with-close-button>
                <template #footer>
                    <CGButton variant="outline" @click="confirmModal=false">{{ $t('General.Cancel') }}</CGButton>
                    <CGButton @click="confirmModal=false; saveAllChanges()">{{ $t('General.Confirm') }}</CGButton>
                </template>
            </Modal>

            <!-- Search and filter -->
            <div class="flex flex-row gap-4 mb-8 justify-end items-center">
                <FormGroup html-for="targetFilterOptions" :label="$t('General.Status')" sr-only>
                    <CGSelect
                        id="targetFilterOptions"
                        v-model="targetStatusFilter"
                        :options="targetStatusOption"
                        class="justify-self-end"
                        @update:modelValue="showTargets"
                    ></CGSelect>
                </FormGroup>
                <label for="searchTarget" class="sr-only">{{ $t('General.Search') }}</label>
                <CGSearchBar id="searchTarget" v-model="searchText" :placeholder="$t('General.TypeToSearch')" @search="loadData" />
            </div>

            <CGTable v-if="targetsShown.length" v-model:sort-key="sortKey" v-model:sort-direction="sortDir" :name="$t('Navbar.Targets')" alternate-color="white">
                <template #head>
                    <TableCell v-for="prop in props" :key="prop.key" :sort-key="prop.key" head>{{ prop.title }}</TableCell>
                    <TableCell v-if="canEditTargetTimeline" head>{{ $t('Analytics.Insight') }}</TableCell>
                </template>
                <TableRow v-for="(target, i) in targetsShown" :key="i" :record="target">
                    <TableCell>
                        <Txt weight="bold">{{ target.name }}</Txt>
                    </TableCell>
                    <TableCell>{{ target.team }}</TableCell>
                    <TableCell>{{ target.sent }}</TableCell>
                    <TableCell>{{ target.clicked }}</TableCell>
                    <TableCell>{{ target.reported }}</TableCell>
                    <TableCell>{{ target.click_rate}}%</TableCell>
                    <TableCell>{{ $t(target.clicked_last? 'General.Yes' : 'General.No') }}</TableCell>
                    <TableCell>{{ $filters.riskGroup(target.risk_group) }}</TableCell>
                    <TableCell>{{ target.subject }}</TableCell>
                    <TableCell v-if="canEditTargetTimeline">
                        <CGButton :ref="'target_' + target.target_id" small variant="ghost" :aria-label="$t('General.Show')" class="text-primary-300 hover:text-violet-700"
                            @click="showTargetTimelineDetails(target)" @keydown.enter="showTargetTimelineDetails(target)">
                            <Icon :size="18" name="eye"/>
                        </CGButton>
                    </TableCell>
                </TableRow>
            </CGTable>

            <div v-else class="my-8">
                <Txt weight="medium">{{ $t('General.TableDataPending') }}</Txt>
            </div>
        </div>
        <Pagination v-if="dataLength" :page="page" :items-per-page="itemsPerPage" :total-items="dataLength" :context="$t('General.TargetsPerPage')" class="mt-2" @page-change="pageChange"/>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

import phishingService from '@/services/phishing.service.js'
import targetService from '@/services/target.service.js'

export default defineComponent({
    props: {
        team: Object,
        apiFilter: Object,
        companyData: Object
    },

    data() {
        return {
            isLoading: false,
            data: [],
            dataLength: 0,
            page: 1,
            targetsShown : [],
            itemsPerPage : 10,
            title: this.$t('Dashboard.Charts.ClickRateByParam', { param : 'target'}),
            props:[ 
                { title: this.$t('Dashboard.Charts.Target'), key: 'name' },
                { title: this.team?.label || this.team?.key, key: 'team' },
                { title: this.$t('Dashboard.Charts.Sent'), key: 'sent' },
                { title: this.$t('Dashboard.Charts.Clicked'), key: 'clicked' },
                { title: this.$t('Dashboard.Charts.Reported'), key: 'reported' },
                { title: this.$t('Dashboard.Charts.ClickRate'), key: 'click_rate' },
                { title: this.$t('Dashboard.Charts.ClickedLastCampaign'), key: 'clicked_last' },
                { title: this.$t('Dashboard.Charts.CyberStrength'), key: 'risk_group' },
                { title: this.$t('Dashboard.Charts.LastTemplateObject'), key: 'subject' } 
            ],
            deviceOptions: [
                { value: null, label: this.$t('General.SelectOption') },
                { value: 1, label: this.$t('Analytics.Devices.Other') },
                { value: 2, label: this.$t('Analytics.Devices.Desktop') },
                { value: 3, label: this.$t('Analytics.Devices.Tablet') },
                { value: 4, label: this.$t('Analytics.Devices.Mobile') }
            ],
            cyberDefenders: 4,
            rareClickers: 3,
            targetStatusFilter: null,
            targetStatusOption: [],
            showTargetModal: false,
            selectedTargetId: null,
            myself: 'clickers',
            searchText: "",
            sortKey: 'click_rate',
            sortDir: 'desc',
            counter: 0,
            debounceTimer: null,
            selTargetRef: null,
            selectedTargetName: null,
            confirmModal: false,
            confirmOverrideRef: null,
            modalTableData: [],
            editingCell: null,
            originalValues: {},
            columnTypes: {
                sent: 'datetime',
                open: 'datetime',
                clicked: 'datetime',
                reported: 'datetime',
                submitted_data: 'datetime',
                phished_device: 'int',
                phished_os: 'varchar',
                phished_browser: 'varchar',
                phished_ip: 'varchar'
            }
        }
    },

    watch: {
        apiFilter: function () {
            this.loadData();
        },
        sortKey(newVal, oldVal) {
            if(oldVal !== newVal) {
                this.page = 1; // Reset to first page on sort change
            }
            this.sortTargets();
        },
        sortDir() {
            this.sortTargets();
        },
    },

    computed: {
        disableSaveButton() {
            return this.modalTableData.some(row => row.constraint || row.ipError);
        },
        canEditTargetTimeline() {
            return this.$checkPermission(['phishing-edit-target-risk']);
        }
    },

    mounted() {
        this.loadData();
    },

    methods:{
        async loadData() {
            try {
                this.counter++;
                this.isLoading = true;

                this.targetStatusOption = [
                    { value: null, label: this.$t('Targets.SuspendedAndActiveOption')},
                    { value: '1', label: this.$t('Targets.ActiveOption')}
                ];

                const context = { searchText: this.searchText, sortBy: this.sortKey, sortDesc: this.sortDir === 'desc' ? true : false };
                // Load data
                const res = await phishingService.getCompanySummary(this.companyData.company_id, { 
                    code: 'clickers', 
                    context,
                    team: this.team.key,
                    ...this.apiFilter });

                this.data = res.data;

                this.sortTargets();
            } catch (error) {
                this.$eventBus.$emit('show-alert', {title: this.$t('General.DataFetchError'), variant: 'danger'});
            } finally {
                if (this.counter > 0) this.counter--;
                
                if (!this.counter) this.isLoading = false;
            }
        },
        async showTargetTimelineDetails(target) {
            try {
                this.$eventBus.$emit('loading', true);
                this.selectedTargetId = target.target_id;
                this.selectedTargetName = target.name;

                const response = await targetService.getTargetTimeline(this.selectedTargetId);
                const result = response.data || [];
                this.modalTableData = result
                    .filter(c => c.type === 1 && c.status === 6)
                    .sort((a, b) => {
                        const getNumber = (val) => {
                            const match = val?.match(/\d+/);
                            return match ? parseInt(match[0], 10) : 0;
                        };
                        return getNumber(a.name) - getNumber(b.name);
                    })
                    .map(r => ({...r, phished_device_label: this.deviceOptions.find(o => o.value !== null && o.value === r.phished_device)?.label || r.phished_device }));
                
                this.originalValues = structuredClone(this.modalTableData);

                this.selTargetRef = this.$refs['target_' + this.selectedTargetId];
                this.showTargetModal = true;
            } catch (error) {
                this.$eventBus.$emit('show-alert', { title: this.$t('General.DataFetchError'), variant: 'danger' });
            } finally {
                this.$eventBus.$emit('loading', false);
            }
        },
        pageChange(page, itemsPerPage) {
            this.page = page;
            this.itemsPerPage = itemsPerPage;
            this.showTargets();
        },
        sortTargets() {
            if (!this.data || !this.sortKey) return;

                const direction = this.sortDir === 'desc' ? -1 : 1;

                this.data.sort((a, b) => {
                    const valA = a[this.sortKey];
                    const valB = b[this.sortKey];

                    // Handle null or undefined safely
                    if (valA == null && valB == null) return 0;
                    if (valA == null) return -1 * direction;
                    if (valB == null) return 1 * direction;

                    // Try numeric comparison first (handles string numbers like "5.5")
                    const numA = Number(valA);
                    const numB = Number(valB);
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return (numA - numB) * direction;
                    }

                    // Fallback: string comparison
                    return ('' + valA).localeCompare('' + valB) * direction;
                });

                this.showTargets();
        },
        showTargets() {
            const start = (this.itemsPerPage*this.page)-this.itemsPerPage;
            const end = this.itemsPerPage*this.page;
            const data = this.targetStatusFilter? this.data.filter(((t) => { return t.deleted != 1 })) : this.data;
            this.dataLength = data.length;
            this.targetsShown = [...data].slice(start, end);

            // Shows cyber defenders only if reporting_enabled is true, else shows defenders as rare
            if(!this.companyData?.reporting_enabled) {
                this.targetsShown.forEach((t) => { 
                    if(t.risk_group == this.cyberDefenders) t.risk_group = this.rareClickers; 
                });
            }
        },
        checkConstraints() {
            const row = this.modalTableData[this.editingCell.row];
            if (row.clicked && row.reported) {
                this.$eventBus.$emit('show-alert', { title: this.$t('Analytics.ClickReportConstraint'), variant: 'warning' });
                return true;
            }
            return false;
        },
        setPhishedDeviceLabel(value) {
            if (value === null) return;

            const option = this.deviceOptions.find(o => o.value === value);
            if (option) {
                const label = option.label;
                const index = this.editingCell.row;
                this.modalTableData[index].phished_device_label = label;
            }
        },
        editCell(rowIndex, columnKey) {
            if(!this.modalTableData[rowIndex].sent && columnKey !== 'sent') return;

            this.editingCell = { row: rowIndex, col: columnKey };
        },
        isEditingCell(rowIndex, columnKey) {
            return this.editingCell && this.editingCell.row === rowIndex && this.editingCell.col === columnKey;
        },
        async saveAllChanges() {
            const updates = [];
            const updatedIndexes = [];

            this.modalTableData.forEach((row, i) => {
                const originalRow = this.originalValues[i];

                const changedRow = {
                    campaign_id: row.campaign_id,
                    modified_by: this.$profile?.account_uuid,
                };

                let hasChanges = false;

                Object.keys(this.columnTypes).forEach(col => {
                    const current = row[col] || null;
                    const original = originalRow?.[col] ?? null;

                    if (current !== original) {
                        changedRow[col] = current;
                        hasChanges = true;
                    }
                });

                if (hasChanges) {
                    updates.push(changedRow);
                    updatedIndexes.push(i);
                }
            });

            try {
                this.$eventBus.$emit('loading', true);

                await targetService.updateTargetTimeline(this.selectedTargetId, this.companyData.company_id, updates);

                // Update originals after success
                updatedIndexes.forEach(i => {
                    this.originalValues[i] = structuredClone(this.modalTableData[i]);
                });

                this.$eventBus.$emit('show-alert', { title: this.$t('Dashboard.Charts.TargetTimelineUpdated'), variant: 'success' });

                this.showTargetModal = false;
            } catch (error) {
                this.$eventBus.$emit('show-alert', { title: this.$t('Dashboard.Charts.TargetTimelineUpdateError'), variant: 'danger' });
            } finally {
                this.$eventBus.$emit('loading', false);
            }
        },
        validateIP(value) {
            if(!value) return true;

            const parts = value.split(".");
            if (parts.length !== 4) return false;

            return parts.every(p => {
                if (!/^\d+$/.test(p)) return false; // only digits
                if (p.length > 1 && p.startsWith("0")) return false; // no leading zeros
                
                const n = Number(p);
                return n >= 0 && n <= 255;
            });
        }
    }
});
</script>

<style scoped lang="scss">
    .analytics-table {
        padding: 20px;
        background-color: white;
        border: solid 1px rgb(229, 231, 235);
        max-height: 650px ;
        overflow: auto;
        position: relative;

        .white-overlay {
            position: absolute;
            top: 0;
            left: 0;
        }
    }
    .timeline-header {
        @apply px-2.5 py-3.5;
    }
    .timeline-row {
        @apply px-2.5 flex items-center cursor-pointer hover:bg-gray-100;
    }
</style>